import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, noop } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { venomHistory } from 'client/utils/history/venom-history';
import {
  ConsumerReviewsAspects,
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
  DEFAULT_FILTER,
  PAGE_SIZES,
} from 'client/data/models/consumer-reviews';
import { buildConsumerReviewsUrl } from 'site-modules/shared/utils/consumer-reviews/url-builder';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { Pagination } from 'site-modules/shared/components/pagination/pagination';
import { ReviewSummaryLinks } from 'site-modules/shared/components/review-summary-links/review-summary-links';
import { ConsumerReviewAspectFilterButtons } from 'site-modules/shared/components/consumer-review-aspect-filter-buttons/consumer-review-aspect-filter-buttons';
import { PageSizeSelect } from 'site-modules/shared/components/page-size-select/page-size-select';
import { ReviewItem } from 'site-modules/consumer-reviews/components/review-item/review-item';
import { StyleSelect } from 'site-modules/consumer-reviews/components/style-select/style-select';
import { SortDropdown } from 'site-modules/consumer-reviews/components/sort-dropdown/sort-dropdown';
import { RatingSelect } from 'site-modules/consumer-reviews/components/rating-select/rating-select';
import { TrackCarValueModule } from 'site-modules/shared/components/track-car-value/track-car-value-module';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { MiniInventoryModule } from 'site-modules/shared/components/mini-inventory-module/mini-inventory-module';

import './reviews-list.scss';

function adUnitsAfterReviews(mobile) {
  return {
    0: <IncentivesAd mobile={mobile} includeMrectAdCall />,
    2: <BuildPriceAd mobile={mobile} includeMrectAdCall wrapperClass="d-flex justify-content-center" />,
    3: (
      <NativeAd
        wrapperClass="mrect mx-auto"
        adNameWired="mrect"
        adNameMobile="mmrect"
        nativeStyle="mediumrectangle"
        showLabel
        wiredBreakpoints={{ md: true }}
        renderOnScroll
      />
    ),
  };
}

function ReviewsListUI({
  consumerReviews,
  vehicle,
  reviewsFilter,
  vehicleFilter,
  setModelValue,
  addLoading,
  isMobile,
  isUsed,
  consumerReviewsAspects,
}) {
  const {
    year,
    make: { name: makeName },
    model: { name: modelName },
  } = vehicle;
  const { rating, aspect } = reviewsFilter;

  const filtersProps = {
    reviewsFilter,
    vehicleFilter,
    addLoading,
  };
  const consumerReviewsEntries = (!!consumerReviews && consumerReviews.reviews) || [];
  const pages = !!consumerReviews && Math.max(1, Math.ceil(consumerReviews.totalReviews / reviewsFilter.pagesize));
  const adUnits = adUnitsAfterReviews(isMobile);
  const containsAspects =
    !!consumerReviewsAspects &&
    (consumerReviewsAspects.pros.length || consumerReviewsAspects.cons.length || consumerReviewsAspects.neutral.length);

  const setAspect = useCallback(
    (newAspect, polarity) => {
      const setAspectDefaultPageSize =
        get(reviewsFilter, 'aspect', null) === null && get(reviewsFilter, 'pagesize', null) < 10;
      const newRating = get(reviewsFilter, 'rating', 0);

      setModelValue(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel, {
        aspect: newAspect,
        rating: newRating,
        pagenum: 1,
        polarity,
        ...(setAspectDefaultPageSize && { pagesize: 10 }),
      });
    },
    [reviewsFilter, setModelValue]
  );

  const clearFilter = useCallback(() => {
    setAspect(null);
  }, [setAspect]);

  const setStarRating = useCallback(
    ({ value: newRating }) => {
      addLoading();

      setTimeout(() => {
        venomHistory.push(
          buildConsumerReviewsUrl(
            {
              ...reviewsFilter,
              pagenum: 1,
              rating: newRating,
            },
            vehicleFilter
          )
        );
      }, 0);
    },
    [reviewsFilter, vehicleFilter, addLoading]
  );

  const buildPageSizeLink = useCallback(
    pageSize => buildConsumerReviewsUrl({ ...reviewsFilter, pagesize: pageSize, pagenum: 1 }, vehicleFilter),
    [reviewsFilter, vehicleFilter]
  );

  const onPaginationClick = useCallback(
    (e, pagenum) => {
      addLoading();

      setTimeout(() => {
        venomHistory.push(
          buildConsumerReviewsUrl(
            {
              ...reviewsFilter,
              pagenum,
            },
            vehicleFilter
          )
        );
      }, 0);
    },
    [addLoading, reviewsFilter, vehicleFilter]
  );

  const paginationProps = {
    currentNum: reviewsFilter.pagenum,
    totalNum: pages,
    linkComponent: 'button',
    onArrowLinkClick: onPaginationClick,
    onPageLinkClick: onPaginationClick,
    numLinkClassname: 'btn btn-link small px-0_5 py-0 fw-normal',
    arrowClassName: 'btn btn-link text-primary-darker size-10 px-0_5',
    trackingId: 'crr_navigate',
  };

  return (
    <Fragment>
      {/* no reviews but there are aspects for that car */}
      {(!!consumerReviewsEntries.length || !!containsAspects) && (
        <Row>
          <Col xs={12} md={4}>
            <StyleSelect {...filtersProps} modelName={modelName} />
          </Col>
          <Col xs={6} md={4} className={classnames({ 'mt-1_5': isMobile })}>
            <RatingSelect setStarRating={setStarRating} rating={rating} />
          </Col>

          <Col xs={6} md={4} className={classnames({ 'mt-1_5': isMobile })}>
            {!aspect && <SortDropdown {...filtersProps} />}
          </Col>
        </Row>
      )}
      <div className="my-1_5 text-center pagination-container">
        {pages > 1 && (
          <Pagination
            {...paginationProps}
            key={`${reviewsFilter.sorting}-top`}
            className="page-num-select mb-1_5 d-flex justify-content-center align-items-baseline"
            listClassName="d-flex justify-content-center align-items-baseline"
            navAriaLabel="Pagination Navigation top"
          />
        )}
      </div>
      {isMobile && <NativeVideoAd useMinHeight mobileOnly />}
      <hr className="my-0" />
      {isUsed && (
        <div className="mt-2 mt-lg-0">
          <TrackCarValueModule btnColor="blue-50" />
        </div>
      )}

      {!!consumerReviewsEntries.length && get(vehicleFilter, 'years.length', 1) > 1 && (
        <div className="mt-1 small text-gray-dark">
          We have a limited number of reviews for the {year} {modelName}, so we&apos;ve included reviews for other years
          of the {modelName} since its last redesign.
        </div>
      )}
      <div>
        {!!(!vehicleFilter.styleId && consumerReviewsAspects && containsAspects) && (
          <ConsumerReviewAspectFilterButtons
            vehicle={vehicle}
            isMobile={isMobile}
            setAspect={setAspect}
            active={aspect}
            isProsConsGrouping
          />
        )}
      </div>
      <div className="aspect-container mb-0_5">
        {!!aspect && (
          <h3 className="heading-4 fw-medium mb-0">
            <Fragment>
              Reviews that mention &ldquo;{aspect.displayName}&rdquo;{' '}
              <Button
                color="link"
                size="lg"
                className="clear-aspect-filter p-0 text-capitalize align-bottom"
                onClick={clearFilter}
              >
                Clear filter
              </Button>
            </Fragment>
          </h3>
        )}
      </div>
      <div aria-label="Consumer Reviews" className="reviews-list">
        {!!consumerReviewsEntries.length && (
          <div aria-label="Consumer Reviews" className="reviews-list">
            {consumerReviewsEntries.map((review, index) => (
              <Fragment key={`review-item-${review.id}`}>
                <ReviewItem vehicle={vehicle} review={review} dataTrackingParent={`edm-entry-review-${index + 1}`} />
                {index === 1 && (
                  <Fragment>
                    <MiniInventoryModule
                      vehicleParams={{
                        makeName: vehicle.make.name,
                        modelName: vehicle.model.name,
                        makeSlug: vehicle.make.slug,
                        modelSlug: vehicle.model.slug,
                        year: vehicle.year,
                      }}
                      isMobile={isMobile}
                    />
                    <hr className="mt-1_5" />
                  </Fragment>
                )}
                {adUnits[index]}
                {!!adUnits[index] && <hr />}
              </Fragment>
            ))}
          </div>
        )}
        {!!consumerReviews && !consumerReviewsEntries.length && !!reviewsFilter.rating && (
          <div>
            There are no {reviewsFilter.rating}-star consumer reviews for the {year} {makeName} {modelName}.
          </div>
        )}
        {/* When consumerReviews is null, the query hasn't returned yet and we should show the loading state.
            Otherwise, consumerReviews.results will return an empty array when no reviews were found. */}
        {!consumerReviews && <div>Loading&hellip;</div>}
      </div>

      <Row>
        <Col md={{ size: 4, offset: 4 }} className="pagination-container">
          {pages > 1 && (
            <Pagination
              {...paginationProps}
              key={`${reviewsFilter.sorting}-bottom`}
              className="page-num-select d-flex justify-content-center align-items-baseline"
              listClassName="d-flex justify-content-center align-items-baseline flex-wrap"
              navAriaLabel="Pagination Navigation bottom"
            />
          )}
        </Col>
        <Col md={4}>
          {consumerReviewsEntries.length > 1 && (
            <PageSizeSelect
              buildPageSizeLink={buildPageSizeLink}
              currentSize={reviewsFilter.pagesize}
              pageSizes={PAGE_SIZES}
              dataTrackingId="crr_items_per_page"
              addLoading={addLoading}
              useVenomHistory
            />
          )}
        </Col>
      </Row>
      <ReviewSummaryLinks vehicle={vehicle} showAllForSaleLink />
    </Fragment>
  );
}

ReviewsListUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  consumerReviews: ConsumerReviewsEntities.ConsumerReviewsContent,
  reviewsFilter: ConsumerReviewsEntities.ReviewsFilter,
  vehicleFilter: ConsumerReviewsEntities.VehicleFilter.isRequired,
  setModelValue: PropTypes.func.isRequired,
  addLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isUsed: PropTypes.bool,
  consumerReviewsAspects: PropTypes.shape({
    pros: PropTypes.arrayOf(ConsumerReviewsAspects),
    cons: PropTypes.arrayOf(ConsumerReviewsAspects),
    neutral: PropTypes.arrayOf(ConsumerReviewsAspects),
  }),
};

ReviewsListUI.defaultProps = {
  consumerReviews: null,
  addLoading: noop,
  reviewsFilter: DEFAULT_FILTER,
  isMobile: false,
  isUsed: false,
  consumerReviewsAspects: null,
};

export const propsAreEqual = (prevProps, { consumerReviews }) => !consumerReviews;
export const ReviewsList = connectToModel(React.memo(ReviewsListUI, propsAreEqual), {
  consumerReviews: bindToPath(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel),
  consumerReviewsAspects: bindToPath(ConsumerReviewsPaths.buildConsumerReviewsAspectsPath(), ConsumerReviewsModel),
});

ReviewsList.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  isUsed: PropTypes.bool,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }),
  consumerReviewsAspects: PropTypes.arrayOf(ConsumerReviewsAspects),
};
ReviewsList.defaultProps = {
  isUsed: false,
  router: {
    location: {
      search: '',
      query: {},
    },
  },
  consumerReviewsAspects: null,
};
